/**
 * ImageUpload - a Clara Vale styled image picker.
 *
 * Uploads the file immediately via the /files/upload/ endpoint,
 * then passes the returned UUID back to the parent via onChange.
 */

import { useRef, useState, useEffect } from 'react'
import { useUploadFile, type UploadedFile } from '@/admin/hooks/use-products'
import { ImagePlus, X, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { cn } from '@/shared/lib/utils'

import { toastApiError } from '@/shared/lib/api-error'
interface ImageUploadProps {
  /** UUID of the currently selected image (if any) */
  value: string | null
  /** Called with the UUID after a successful upload, or null to clear */
  onChange: (uuid: string | null, file?: UploadedFile) => void
  /** Preview URL for the current image */
  previewUrl?: string | null
  /** use_case sent to the upload endpoint */
  useCase?: string
  /** Label above the input */
  label?: string
  /** Optional class override for the rendered preview image */
  previewClassName?: string
  /**
   * Optional custom uploader. When provided it replaces the default
   * `/files/upload/` call - used by surfaces that lack an org-user audience
   * (e.g. the staff portal, which uploads via a staff-scoped endpoint).
   */
  uploadFn?: (args: { file: File; use_case: string }) => Promise<UploadedFile>
}

export function ImageUpload({
  value,
  onChange,
  previewUrl,
  useCase = 'PRODUCT_IMAGE',
  label = 'Image',
  previewClassName,
  uploadFn,
}: ImageUploadProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const upload = useUploadFile()
  const [localPreview, setLocalPreview] = useState<string | null>(null)
  const [customPending, setCustomPending] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  // Revoke the object URL created for the local preview to avoid leaking it
  // (fires when the preview changes and on unmount).
  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview)
    }
  }, [localPreview])

  const preview = localPreview ?? previewUrl
  const isUploading = uploadFn ? customPending : upload.isPending

  async function handleFile(file: File) {
    // Instant local preview
    setLocalPreview(URL.createObjectURL(file))

    try {
      const result = uploadFn
        ? await runCustomUpload(file)
        : await upload.mutateAsync({ file, use_case: useCase })
      onChange(result.uuid, result)
    } catch (err) {
      toastApiError(err, t('imageUpload.uploadFailed'));
      setLocalPreview(null)
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    await handleFile(file)
    // Reset input so re-selecting the same file works
    if (inputRef.current) inputRef.current.value = ''
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setIsDragging(false)
    if (isUploading) return
    const file = e.dataTransfer.files?.[0]
    if (file && file.type.startsWith('image/')) void handleFile(file)
  }

  async function runCustomUpload(file: File): Promise<UploadedFile> {
    setCustomPending(true)
    try {
      return await uploadFn!({ file, use_case: useCase })
    } finally {
      setCustomPending(false)
    }
  }

  function handleClear() {
    onChange(null)
    setLocalPreview(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault()
        if (!isUploading) setIsDragging(true)
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        setIsDragging(false)
      }}
      onDrop={handleDrop}
    >
      {label && <Label className="clara-section-label mb-1.5 block">{label}</Label>}
      {preview || value ? (
        /* ── Preview state ── */
        (<div className="group relative w-full overflow-hidden rounded-[8px] border border-input bg-[var(--bg-subtle)]">
          {preview ? (
            <img
              src={preview}
              alt=""
              className={cn('max-h-[200px] w-full object-contain p-3', previewClassName)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-[var(--bg-subtle)]">
              <span className="text-[11px] text-[var(--ink-3)]">
                {t('imageUpload.uploaded')}
              </span>
            </div>
          )}
          {/* Overlay actions */}
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
            <Button
              variant="ghost"
              onClick={() => inputRef.current?.click()}
              className="cursor-pointer rounded-[4px] bg-[var(--bg)] px-3 py-1.5 text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]"
            >
              {t('imageUpload.replace')}
            </Button>
            <Button
              variant="ghost"
              onClick={handleClear}
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-[4px] bg-[var(--bg)] text-[var(--ink-3)]"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          {/* Upload spinner */}
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <Loader2 className="h-5 w-5 animate-spin text-[var(--bg)]" />
            </div>
          )}
        </div>)
      ) : (
        /* ── Empty state ── */
        (<Button
          variant="ghost"
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
          className={cn(
            'w-full h-24 rounded-[8px] flex flex-col items-center justify-center gap-1.5 transition-all duration-150 cursor-pointer border border-dashed border-input text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]',
            isDragging && 'border-[var(--green)] bg-[var(--bg-subtle)]',
          )}
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <ImagePlus className="h-4 w-4" />
              <span className="text-[11px]">{t('imageUpload.upload')}</span>
            </>
          )}
        </Button>)
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/svg+xml"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}
